import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "@/src/components/ui/button";
import { Booking } from "./model";
import { fetch } from "@/src/lib/fetch";
import { paymentsFetch } from "@/src/lib/paymentsFetch";
import { cn } from "@/src/lib/utils";
import { LinkDealDialog } from "./LinkDealDialog";

/** Page-level right rail (v14 design): quick actions, People and At a glance, beside every tab. */
type People = { broker: { name: string | null; email: string | null } | null; ops: { name: string | null; email: string | null } | null; opsSource: string };

const fmtMoney = (v: number, code?: string | null) =>
  new Intl.NumberFormat("en-EN", { style: "currency", currency: code || "EUR", maximumFractionDigits: 0 }).format(v || 0);
const sum = (rows: any[] | undefined, k: "paidSoFar" | "amount") => (rows ?? []).reduce((t, r) => t + (Number(r?.[k]) || 0), 0);

// Same keys as the header and the Payments section — React Query serves all three from one request.
const useSchedule = (bookingId: string, payer: "Customer" | "Operator") =>
  useQuery({
    queryKey: [payer === "Customer" ? "paymentScheduleCustomerData" : "paymentScheduleOperatorData", bookingId],
    queryFn: () => paymentsFetch<{ data: any[] }>(`/api/payment-schedule-step?page=1&limit=10&orderBy=Booking+date&payerType=${payer}&bookingRef=${bookingId}&operatorRef=null`),
    enabled: !!bookingId,
  });

function settled(rows: any[] | undefined): { label: string; cls: string } {
  const total = sum(rows, "amount"), paid = sum(rows, "paidSoFar");
  if (total <= 0) return { label: "No schedule", cls: "bg-slate-100 text-slate-500" };
  if (paid >= total - 0.01) return { label: "Yes", cls: "bg-green-100 text-green-800" };
  if (paid > 0.01) return { label: "Partial", cls: "bg-amber-100 text-amber-800" };
  return { label: "Pending", cls: "bg-amber-100 text-amber-800" };
}

const SOURCE_LABEL: Record<string, string> = { platform: "Client booked online", broker_created: "Broker-created", imported: "Imported from ops" };

export function BookingRail({ booking, operatorName, supplierLabel, onGo }: { booking: Booking; operatorName?: string | null; supplierLabel: string; onGo: (tab: string) => void }) {
  const [people, setPeople] = useState<People | null>(null);
  const [linkDeal, setLinkDeal] = useState(false);
  const dealMissing = !booking.hubspotId || !/^\d+$/.test(String(booking.hubspotId));
  useEffect(() => { fetch<{ data: People }>(`/api/bookings/${booking.id}/people`).then((r: any) => setPeople(r.data)).catch(() => setPeople(null)); }, [booking.id]);
  const { data: client } = useSchedule(booking.id, "Customer");
  const { data: supplier } = useSchedule(booking.id, "Operator");
  const contractUrl = (booking as any).contractFile?.url as string | undefined;
  const dealLink = booking.offer?.dealLink && !/undefined/.test(booking.offer.dealLink) ? booking.offer.dealLink : null;
  const initials = (f?: string, l?: string) => `${(f ?? "")[0] ?? ""}${(l ?? "")[0] ?? ""}`.toUpperCase();
  const customer = [booking.customerFirstName, booking.customerLastName].filter(Boolean).join(" ") || "—";
  const brokerName = people?.broker?.name ?? (booking.broker ? `${(booking.broker as any).firstName ?? ""} ${(booking.broker as any).lastName ?? ""}`.trim() : "") ?? "";
  const broker = brokerName || (people ? "—" : "…");
  const source = SOURCE_LABEL[String(booking.source ?? "")] ?? (booking.importDetails ? "Imported from ops" : booking.offer ? "Broker-created" : "—");
  const start = booking.startDate ?? booking.offerYacht?.startDate ?? null;
  const end = booking.endDate ?? booking.offerYacht?.endDate ?? null;
  const daysTo = start ? Math.ceil((new Date(start).getTime() - Date.now()) / 86400000) : null;
  const fmtDay = (d: string | null) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
  const nights = start && end ? Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86400000) : null;
  const departure =
    daysTo == null ? "—" : daysTo > 0 ? String(daysTo) : end && new Date(end).getTime() > Date.now() ? "On charter" : `Departed ${Math.abs(daysTo)}d ago`;
  const total = Number(booking.pricing?.totalPrice) || sum(client?.data, "amount");
  const clientState = settled(client?.data), supplierState = settled(supplier?.data);

  const Row = ({ k, v, ini }: { k: string; v: React.ReactNode; ini?: string }) => (
    <div className="flex items-center justify-between border-b border-slate-100 py-3 text-base last:border-0">
      <span className="text-slate-500">{k}</span>
      <span className="flex items-center gap-2">{ini && <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-[11px] font-semibold text-blue-800">{ini}</span>}{v}</span>
    </div>
  );
  const Pill = ({ label, cls }: { label: string; cls: string }) => <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", cls)}>{label}</span>;
  return (
    <div className="flex w-full flex-col gap-4">
      <Card className="flex w-full flex-col gap-2">
        <Button className="w-full" onClick={() => (contractUrl ? window.open(contractUrl, "_blank", "noopener") : onGo("contract"))}>Open contract</Button>
        <Button className="w-full" variant="outline" onClick={() => onGo("money")}>$ Record payment</Button>
        {dealLink && <Button className="w-full" variant="outline" onClick={() => window.open(dealLink, "_blank", "noopener")}>↗ Open contact in HubSpot</Button>}
      </Card>
      <Card className="w-full">
        <h3 className="mb-1 text-lg font-semibold">People</h3>
        <Row k="Customer" v={customer} ini={initials(booking.customerFirstName, booking.customerLastName)} />
        <Row k="Broker" v={broker} ini={brokerName ? initials(...(brokerName.split(" ") as [string, string])) : undefined} />
        <Row k="Ops" v={people?.ops?.name ?? (people ? <span className="text-slate-400" title={people.opsSource}>{people.opsSource === "no ticket on the deal" ? "no ticket yet" : "—"}</span> : "…")} ini={people?.ops?.name ? initials(...(people.ops.name.split(" ") as [string, string])) : undefined} />
        <Row k="Operator" v={operatorName ?? supplierLabel} />
        <Row k="Source" v={source} />
        <Row
          k="HubSpot deal"
          v={dealMissing
            ? <span className="flex items-center gap-2"><span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">Missing</span><button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => setLinkDeal(true)}>Link…</button></span>
            : <a className="text-sm text-blue-600 hover:underline" href={dealLink ?? `https://app-eu1.hubspot.com/contacts/143390230/record/0-3/${booking.hubspotId}`} target="_blank" rel="noreferrer noopener">{booking.hubspotId} ↗</a>}
        />
        {linkDeal && <LinkDealDialog bookingId={booking.id} bookingLabel={booking.slug} initialQuery={booking.customerEmail ?? customer} onClose={() => setLinkDeal(false)} onLinked={() => window.location.reload()} />}
      </Card>
      <Card className="w-full">
        <h3 className="mb-1 text-lg font-semibold">At a glance</h3>
        <Row k="Charter dates" v={<span className="text-right text-sm">{fmtDay(start)}{end ? ` – ${fmtDay(end)}` : ""}{nights ? <span className="block text-xs text-slate-500">{nights} night{nights === 1 ? "" : "s"}</span> : null}</span>} />
        <Row k="Total charter" v={<span className="font-semibold tabular-nums">{fmtMoney(total, booking.currency?.code)}</span>} />
        <Row k="Client settled" v={<Pill label={clientState.label} cls={clientState.cls} />} />
        <Row k="Supplier settled" v={<Pill label={supplierState.label} cls={supplierState.cls} />} />
        <Row k="Days to departure" v={departure} />
      </Card>
    </div>
  );
}
